import { LEADERBOARD_BOARD, LEADERBOARD_PERIOD } from "@shared/models";

/**
 * Design-fix plan: the ranked-board's empty-state copy, pinned per board rather than composed from
 * conditionals -- moved here (out of `LeaderboardsContainer.tsx`) so `OverviewLeaderboard`'s own card
 * can reuse the exact same wording instead of hardcoding a second, period-blind copy of it. `board`'s
 * own callers type it as the FULL `LEADERBOARD_BOARD` enum (shared with crowns' own board dimension,
 * which is always CROWNS), even though this copy only ever renders for RECEIVED/GIVEN in practice --
 * so this map is deliberately `Partial`. Indexing a non-`Partial` two-key `Record` with a value of
 * the wider three-member enum type is exactly the kind of mistake `tsc`/`npm run build` catches and
 * this repo's babel-jest test runner (types stripped) does not.
 */
const EMPTY_BOARD_COPY: Partial<Record<LEADERBOARD_BOARD, string>> = {
  [LEADERBOARD_BOARD.RECEIVED]: "received a shout-out",
  [LEADERBOARD_BOARD.GIVEN]: "given a shout-out",
};

/** Pinned per period -- ALL_TIME reads "yet" rather than "this all-time", the one suffix that isn't "this <period>". */
const EMPTY_PERIOD_SUFFIX: Record<LEADERBOARD_PERIOD, string> = {
  [LEADERBOARD_PERIOD.WEEK]: " this week",
  [LEADERBOARD_PERIOD.MONTH]: " this month",
  [LEADERBOARD_PERIOD.QUARTER]: " this quarter",
  [LEADERBOARD_PERIOD.ALL_TIME]: " yet",
};

/**
 * The ranked leaderboard's empty-state copy for a given board/period. Shared by the Leaderboard tab
 * (`LeaderboardsContainer`) and the Overview rail card (`OverviewLeaderboard`) so both name the actual
 * period the viewer is looking at -- a board-only copy (e.g. a hardcoded "...yet.") reads as all-time
 * and is wrong on any other period.
 *
 * @param board - the ranked board (RECEIVED or GIVEN; any other value renders the board segment as
 *   `""` via `?? ""`, since `EMPTY_BOARD_COPY` has no CROWNS/TEAM entry).
 * @param period - the selected period; appended as its own suffix.
 * @returns the exact copy to render, e.g. `"No one's received a shout-out this week — be the first."`
 */
export const leaderboardEmptyCopy = (board: LEADERBOARD_BOARD, period: LEADERBOARD_PERIOD): string =>
  `No one's ${EMPTY_BOARD_COPY[board] ?? ""}${EMPTY_PERIOD_SUFFIX[period]} — be the first.`;

/**
 * The pinned "You" row's replacement copy when the viewer has no score at all in the selected
 * board/period -- the api's own `viewer: null` (GetLeaderboardResults' own schema comment: `null`,
 * "never a rank of 0", when `computeViewerRank` finds no row for them). Shares `EMPTY_PERIOD_SUFFIX`
 * with `leaderboardEmptyCopy` above rather than a second, hand-written period vocabulary, so both
 * strings name ALL_TIME the same way ("yet", not "this all-time").
 *
 * @param period - the selected period.
 * @returns the exact copy to render, e.g. `"Not ranked this quarter — your first Spark puts you on the board."`
 */
export const leaderboardViewerUnrankedCopy = (period: LEADERBOARD_PERIOD): string =>
  `Not ranked${EMPTY_PERIOD_SUFFIX[period]} — your first Spark puts you on the board.`;

/**
 * The short form of `leaderboardViewerUnrankedCopy` above, for the Overview rail card's footer slot --
 * a narrow surface whose existing footer register is already terse (`"5 and you move up a place"`), so
 * this states only the fact ("Not ranked this quarter.") and drops the "your first Spark..." call to
 * action; that belongs on the Leaderboard tab, which has the room for it. Shares `EMPTY_PERIOD_SUFFIX`
 * with the two exports above rather than a second, hand-written period vocabulary, so this reads
 * ALL_TIME the same way they do ("yet", not "this all-time").
 *
 * @param period - the selected period.
 * @returns the exact copy to render, e.g. `"Not ranked this quarter."`
 */
export const leaderboardViewerUnrankedShortCopy = (period: LEADERBOARD_PERIOD): string =>
  `Not ranked${EMPTY_PERIOD_SUFFIX[period]}.`;

/**
 * 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: "Spread your Sparks"' own empty-state copy -- shown once the
 * fetch has settled with zero suggested collaborators (a genuinely correct outcome, e.g. a three-person
 * department, or someone who only logged holiday this quarter -- see the phase doc §3). Lives here,
 * alongside the ranked board's own empty/unranked copy above, rather than as a literal inside
 * `SparkSpreadSection.tsx` -- this module is this app's one place for the Leaderboard tab's section
 * copy.
 */
export const SPARK_SPREAD_EMPTY_STATE_MESSAGE = "No teammates to suggest right now — check back later.";

/**
 * 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3, browser-verify fix: `SparkSpreadSection`'s OWN failure message
 * -- deliberately NOT `LeaderboardLoadStatus`'s exported `LEADERBOARD_LOAD_FAILED_MESSAGE` ("We couldn't
 * load the leaderboard..."), which names the wrong thing when only this section's fetch fails while the
 * ranked board above it is visibly on screen and fine. `LeaderboardLoadStatus`'s `failureMessage` prop
 * exists precisely so each caller supplies its own -- the crowns rail already does this with its own
 * inline literal; this section's copy lives here instead, alongside its own empty-state message above,
 * since both are this section's copy and this module is where that copy already lives.
 */
export const SPARK_SPREAD_LOAD_FAILED_MESSAGE = "We couldn't load your teammates. Refresh to try again.";

/**
 * L5 (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md), follow-up 111: the value filter chips' own catalog-fetch
 * failure message -- `getCoreValues.failure` had no reducer handler at all until this phase
 * (RecognitionState.interface.tsx's own comment on `coreValuesLoadFailed`), so a failed fetch and an
 * org with zero core values used to be indistinguishable: both rendered an empty chip row with nothing
 * to explain it. Shown in the chip row's own slot in place of the (silently empty) `FilterChips`.
 */
export const LEADERBOARD_VALUE_CHIPS_LOAD_FAILED_MESSAGE = "We couldn't load the value filters. Refresh to try again.";

/**
 * Review follow-up (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8): the "Your all-time" rail card's own failure
 * message. `YourAllTimeCard.tsx` renders "—" for any null field -- the CORRECT reading for a brand-new
 * employee with no history, but the WRONG one for a dedicated ALL_TIME rank/summary fetch that failed
 * outright, which used to render the exact same dashes with nothing to tell the two apart. Shown via
 * `LeaderboardLoadStatus`, in place of the card, whenever either fetch's own failure flag is set --
 * same "failed fetch must not look like legitimately-empty data" fix `getCoreValues` already got in
 * this phase (follow-up 111, above).
 */
export const LEADERBOARD_ALL_TIME_LOAD_FAILED_MESSAGE = "We couldn't load your all-time stats. Refresh to try again.";
